<template>
    <div id="main" class="is-flex">
        <container-loader v-if="isLoading && !$store.getters.isLoading" />
        <div class="page mb-0">
            <div class="vertical-flow">
                <!-- BREADCRUMB -->
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">{{ $t('objetivo-vendas') }}</h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ $t('vendas') }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{ $t('cadastros') }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{ $t('objetivo-vendas') }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            v-if="permissao.update"
                            class="save-btn"
                            type="button"
                            @click="saveData"
                            :disabled="isSaving || isLoading"
                        >
                            {{ $t('salvar') }}
                        </button>
                    </div>
                </div>
                <div class="columns is-multiline search-filters">
                    <div class="column">
                        <search-bar :onSearch="debouncedOnSearch" />
                    </div>
                    <div class="column is-one-fifth">
                        <label for="inicio">{{ $t('inicio') }}</label>
                        <b-datepicker
                            v-model="mesIni"
                            class="dpicker-filter"
                            type="month"
                            position="is-bottom-left"
                            :month-names="[
                                $t('january'),
                                $t('february'),
                                $t('march'),
                                $t('april'),
                                $t('may'),
                                $t('june'),
                                $t('july'),
                                $t('august'),
                                $t('september'),
                                $t('october'),
                                $t('november'),
                                $t('december'),
                            ]"
                            :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                            :max-date="mesFim"
                            :mobile-native="false"
                            icon="calendar-today"
                        />
                    </div>
                    <div class="column is-one-fifth">
                        <label for="fim">{{ $t('fim') }}</label>
                        <!-- Não alterar b-datepicker para verified-date-picker! O formato aqui é MM/YYYY, enquanto
                        o verified-date-picker tem formato DD/MM/YYYY @70513 -->
                        <b-datepicker
                            v-model="mesFim"
                            class="dpicker-filter"
                            type="month"
                            position="is-bottom-left"
                            :month-names="[
                                $t('january'),
                                $t('february'),
                                $t('march'),
                                $t('april'),
                                $t('may'),
                                $t('june'),
                                $t('july'),
                                $t('august'),
                                $t('september'),
                                $t('october'),
                                $t('november'),
                                $t('december'),
                            ]"
                            :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                            :min-date="mesIni"
                            :max-date="$moment(mesIni).add(3, 'years').toDate()"
                            :mobile-native="false"
                            icon="calendar-today"
                        />
                    </div>
                    <div class="column is-narrow is-flex is-flex-vbottom">
                        <button
                            class="save-btn top-btn"
                            @click="loadData()"
                            :disabled="isSaving || isLoading"
                        >
                            {{ $t('pesquisar') }}
                        </button>
                    </div>
                </div>

                <!-- TABELA -->
                <div class="registry-wrapper">
                    <empty-table-message v-if="!isLoading && isEmpty" />
                    <b-table
                        v-else
                        ref="table"
                        class="data-table has-sticky-footer"
                        id="objetivo-vendas-table"
                        height="calc(100vh - 258px)"
                        :key="tableKey"
                        :data="displayTableData"
                        @sort="updateSortColumns"
                        sticky-header
                        scrollable
                        backend-sorting
                    >
                        <b-table-column
                            v-for="(f, i) in tableFields"
                            class="td-center"
                            :ref="f.key"
                            :key="i"
                            :field="f.key"
                            :label="f.key != 'excluir' ? $t(f.label) : ''"
                            :width="f.width"
                            :centered="f.centered"
                            :sortable="f.sortable"
                            :sticky="f.sticky"
                            v-slot="props"
                        >
                            <div v-if="f.key == 'idusuario'">
                                {{ props.row.usuario.idusuario }}
                            </div>
                            <div v-else-if="f.key == 'idusuarioerp'">
                                {{ props.row.usuario.idusuarioerp }}
                            </div>
                            <div v-else-if="f.key == 'nome'">
                                {{ props.row.usuario.nome }}
                            </div>
                            <div v-else>
                                {{ props.row[f.key] }}
                            </div>
                        </b-table-column>

                        <b-table-column
                            v-for="(m, i) in monthFields"
                            class="td-center"
                            :key="`${i}${m.mes}${m.ano}`"
                            :label="`${`0${m.mes}`.slice(-2)}/${m.ano}`"
                            :field="`${m.mes}/${m.ano}`"
                            :width="monthColumnWidth"
                            centered
                            v-slot="props"
                        >
                            <!-- renderiza sem input, se for mês no passado -->
                            <div v-if="checkIfBeforeCurrentMonth(m.mes, m.ano)">
                                <!-- renderiza o valor se ele existir -->
                                <span
                                    v-if="
                                        (
                                            props.row.objetivos.find(
                                                (e) => e.mes == m.mes && e.ano == m.ano
                                            ) || {}
                                        ).objetivo
                                    "
                                >
                                    {{
                                        getDecimal(
                                            (
                                                props.row.objetivos.find(
                                                    (e) => e.mes == m.mes && e.ano == m.ano
                                                ) || {}
                                            ).objetivo || 0,
                                            2, true
                                        )
                                    }}
                                </span>
                                <!-- renderiza os asteriscos, @68736 requisito 6 -->
                                <span v-else>
                                    <b-icon icon="asterisk" size="is-small" type="is-dark" />
                                    <b-icon icon="asterisk" size="is-small" type="is-dark" />
                                    <b-icon icon="asterisk" size="is-small" type="is-dark" />
                                </span>
                            </div>
                            <!-- disponibiliza o campo para edição, conforme @68736 requisito 7 -->
                            <money
                                v-else
                                class="money-table-inputs"
                                :key="`${props.row.usuario.idusuario}-${m.mes}-${m.ano}`"
                                :value="
                                    (
                                        props.row.objetivos.find(
                                            (e) => e.mes == m.mes && e.ano == m.ano
                                        ) || {}
                                    ).objetivo || 0
                                "
                                :precision="2"
                                :tabindex="`${i + 1}`"
                                :min="0"
                                :max="999999999.99"
                                :disabled="isSaving"
                                v-input-initialized="{ row: props.row, m }"
                                prefix="R$ "
                                decimal=","
                                thousands="."
                            />
                            <!-- tabindex para permitir que, ao dar tab, passe para a linha de baixo. @68736 requisito 14 -->
                        </b-table-column>
                        <template slot="footer">
                            <th colspan="2" scope="row">
                                {{ $t('total-geral') }}: {{ getDecimal(totalSum, 2, true) }}
                            </th>
                            <th class="is-sticky" scope="rowgroup">
                                {{ pagination.totalRows }} {{ $t('colaboradores') }}
                                <br />
                                {{
                                    query && query != ''
                                        ? getDecimal(totalSumColaboradores, 2, true)
                                        : getDecimal(totalSum, 2, true)
                                }}
                            </th>
                            <th
                                class="has-text-centered td-center"
                                scope="col"
                                v-for="(m, i) in monthFields"
                                :key="i"
                            >
                                {{
                                    getDecimal(
                                        (
                                            totalPorMesAno.find(
                                                (e) => e.mes == m.mes && e.ano == m.ano
                                            ) || {}
                                        ).valor,
                                        2, true
                                    )
                                }}
                            </th>
                        </template>
                    </b-table>
                    <PaginationViewer
                        class="mb-0"
                        :resetPagination="pagination.resetPagination"
                        :totalItems="pagination.totalRows"
                        :perPageOptions="pagination.perPageOptions"
                        :optionStarted="pagination.optionStarted"
                        @change="updatePagination"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UsuarioObjetivoVendaService from '@/services/UsuarioObjetivoVendaService';
import ContainerLoader from '@/components/content/loader/ContainerLoader';

export default {
    // @84579 - Método para verificar alterações antes de mudar de rota. Caso tenha, será mostrada a mensagem de alerta.
    beforeRouteLeave(to, from, next) {
        if (this.isChanged) {
            this.promptChangeTab(next);
        } else if (this.listaAlterados && this.listaAlterados.length > 0) {
            this.promptComfirmSair(next);
        } else {
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            service: new UsuarioObjetivoVendaService(this.$http, this.$router),
            query: '',
            maxValue: 999999999,
            isAlterado: false,
            isUpdateVendaHeader: true,
            sortColumn: '',
            lastInputValue: null,
            sortDirection: 'asc',
            mesIni: this.$moment().subtract(1, 'month').date(1).toDate(),
            mesFim: this.$moment().add(1, 'month').date(1).toDate(),
            isLoading: false,
            isEmpty: false,
            isSaving: false,
            isChanged: false,
            debouncedOnSearch: null,
            inputInitialized: false,
            sumForMonth: 0,
            totalSum: 0,
            totalSumColaboradores: 0,
            totalGeralColaboradores: [],
            totalPorMesAno: [],
            objetivoVendaHeader: [],
            objetivoVendaHeaderOld: [],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: 10,
                totalRows: 0,
                currentPage: 1,
            },
            /* Dados da tabela sem filtro e ordenação */
            tableData: [],
            /*  Dados da tabela com filtro e paginação
                    Escolheu-se manter separado pois, ao deixar tudo no mesmo objeto e com a ordenação de uma coluna ativa,
                    a ordem das linhas estava mudando conforme a informação era digitada e isto causava problemas de
                    atualização de valores. */
            displayTableData: [],
            listaAlterados: [],
            /*  Quando este valor mudar, será forçado a atualização das colunas do b-table */
            tableKey: Math.random(),
            /*  Colunas fixas da tabela */
            tableFields: [
                {
                    key: 'idusuario',
                    label: 'id',
                    width: 100,
                    sticky: true,
                    centered: true,
                    sortable: true,
                },
                {
                    key: 'idusuarioerp',
                    label: 'codigo',
                    width: 150,
                    sticky: true,
                    centered: true,
                    sortable: true,
                },
                {
                    key: 'nome',
                    label: 'colaborador',
                    sticky: true,
                    width: 300,
                    sortable: true,
                },
            ],
            /* Largura da coluna de mẽs */
            monthColumnWidth: 200,
            /* Tolerância para verificação de largura da tabela para permitir o scroll */
            widthTolerance: 30,
            /* Colunas variáveis da tabela, gerado no método generateTableColumnsFromInterval */
            monthFields: [],
            permissao: {},
        };
    },
    created() {
        this.permissao = this.hasPermissionAccess('vendas', 'objetivos', 'venda-objetivo-venda');

        !this.permissao.read &&
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'objetivo-vendas' },
            });
        this.debouncedOnSearch = this.debounce(this.onSearch, 1000);
        //this.resetVariaveis();
        this.adjustTableWidth();
        //this.updateDisplayTable();
        this.loadData();
    },
    mounted() {
        this.inputInitialized = true;
    },
    // IMPORTANTE - Esse ajuste foi feito para que ao invés de usar o @input que estava disparando evento quando o componente era montado, ele usa esse cara
    // Validar futuramente a necessidade de remover esse cara pois foi uma solução para a as varias implementações que foram feitas de forma errada nessa tela
    directives: {
        'input-initialized': {
            inserted(el, binding, vnode) {
                const { value, expression } = binding;
                if (vnode.context.inputInitialized) {
                    el.addEventListener('input', (event) => {
                        if (event.isTrusted) {
                            let valor = event.target.value.replace('R$ ', '');
                            valor = valor.trim().replaceAll('.', '').replaceAll(',', '.');

                            if (valor > vnode.context.maxValue) {
                                event.target.value = this.getDecimal(vnode.context.maxValue, null, true);
                                valor = vnode.context.maxValue;
                            }

                            vnode.context.updateValor(
                                value.row.usuario.id,
                                value.m.mes,
                                value.m.ano,
                                Number(valor)
                            );
                        }
                    });
                }
            },
        },
    },
    methods: {
        promptComfirmSair(next) {
            this.$root.confirm({
                message: this.$t('msg-back-alteracoes-perdidas'),
                onConfirm: (_) => {
                    next();
                },
                onCancel: (_) => {
                    next(false);
                },
            });
        },
        onSearch(query, isOnSearch = true) {
            this.query = query.toLocaleLowerCase();
            this.loadData(this.pagination.currentPage, isOnSearch);
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;

            this.loadData(value.currentPage, false);
        },
        updateValor(id, mes, ano, valor) {
            this.isAlterado = true;
            const objAlterado = {
                idusuario: id,
                mes,
                ano,
                valor,
            };

            const indexObjetoFiltrado = this.listaAlterados.findIndex(
                (obj) =>
                    obj.idusuario === objAlterado.idusuario &&
                    obj.mes === objAlterado.mes &&
                    obj.ano === objAlterado.ano
            );
            if (indexObjetoFiltrado !== -1) {
                this.listaAlterados[indexObjetoFiltrado] = objAlterado;
            } else {
                this.listaAlterados.push(objAlterado);
                this.atualizarHeaderEMetas(mes, ano, id);
            }
            this.calcularTotalDosMeses();
        },
        loadData(currentPage = 1, isOnSearch = false) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;

            const mesInicial = this.$moment(this.mesIni).format('YYYY-MM-DD');
            const mesFinal = this.$moment(this.mesFim).format('YYYY-MM-DD');

            this.isLoading = true;
            let validate = true;
            this.service
                .findAll(
                    1,
                    mesInicial,
                    mesFinal,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.sortDirection,
                    this.sortColumn,
                    this.query
                )
                .then(async (value) => {
                    if (
                        this.pagination.totalRows != value.totalElements &&
                        isOnSearch &&
                        this.listaAlterados.length != 0
                    ) {
                        validate = await this.promptComfirmPesquisa();
                    }
                    if (!validate) {
                        return;
                    }

                    if (isOnSearch || this.listaAlterados.length == 0) {
                        this.listaAlterados = [];
                        this.tableData = value.content;
                    } else {
                        this.tableData = this.replaceAlterados(value.content);
                    }
                    this.pagination.totalRows = value.totalElements;
                    this.generateTableColumnsFromInterval();
                    this.updateDisplayTable();
                })
                .catch((e) => {
                    console.error('Erro ao carregar objetivos de venda', e);
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-objetivo-load-error');
                })
                .finally((_) => {
                    if (validate) {
                        this.findObjetivoVendaHeader(isOnSearch);
                    }
                    this.isLoading = false;
                    this.isChanged = false;
                    this.inputInitialized = true;
                });
        },
        promptComfirmPesquisa() {
            return new Promise((resolve) => {
                this.$root.confirm({
                    message: this.$t('msg-item-alterado-objetivo-venda'),
                    onConfirm: (_) => {
                        this.saveData();
                        resolve(true);
                    },
                    onCancel: (_) => {
                        resolve(false);
                    },
                });
            });
        },
        replaceAlterados(value) {
            this.listaAlterados.forEach((obj) => {
                value.forEach((vendaUser) => {
                    const idusuario = vendaUser.usuario.idusuario;
                    if (vendaUser.objetivos && vendaUser.objetivos.length > 0) {
                        vendaUser.objetivos.forEach((item) => {
                            if (
                                idusuario === obj.idusuario &&
                                item.mes === obj.mes &&
                                item.ano === obj.ano
                            ) {
                                item.objetivo = obj.valor;
                            }
                        });

                        const linha = this.listaAlterados.filter(
                            (item) => item.idusuario == idusuario
                        );
                        if (vendaUser.objetivos.length <= linha.length) {
                            linha.forEach((comp) => {
                                vendaUser.objetivos.push({
                                    mes: comp.mes,
                                    ano: comp.ano,
                                    objetivo: obj.valor,
                                });
                            });
                        }
                    } else if (idusuario === obj.idusuario) {
                        const linha = this.listaAlterados.filter(
                            (item) => item.idusuario == idusuario
                        );
                        vendaUser.objetivos = [];
                        linha.forEach((comp) => {
                            vendaUser.objetivos.push({
                                mes: comp.mes,
                                ano: comp.ano,
                                objetivo: obj.valor,
                            });
                        });
                    }
                });
            });
            return value;
        },
        findObjetivoVendaHeader(isOnSearch = false) {
            const mesInicial = this.$moment(this.mesIni).format('YYYY-MM-DD');
            const mesFinal = this.$moment(this.mesFim).format('YYYY-MM-DD');

            this.isLoading = true;
            this.service
                .findObjetivoVendaHeader(mesInicial, mesFinal, isOnSearch ? this.query : '')
                .then((value) => {
                    if (!isOnSearch && (!this.query || this.query != '')) {
                        this.objetivoVendaHeaderOld = value.content;
                    }
                    this.objetivoVendaHeader = value.content;
                    this.totalGeralColaboradores = value.content;
                })
                .catch((e) => {
                    console.error('Erro ao carregar objetivos de venda', e);
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-objetivo-load-error');
                })
                .finally((_) => {
                    if (isOnSearch) {
                        this.calcularTotalMesesColaborador(isOnSearch);
                    } else {
                        this.calcularTotalDosMeses();
                    }
                    this.isLoading = false;
                    this.isChanged = false;
                });
        },
        calcularTotalMesesColaborador(isOnSearch = false) {
            this.totalPorMesAno = this.monthFields.map((obj) => {
                return this.getObjSumMonth(obj.mes, obj.ano);
            });

            let totalTmp = 0;
            this.totalGeralColaboradores.forEach((obj) => {
                totalTmp = totalTmp + obj.totalMes;
            });
            this.totalSumColaboradores = isOnSearch ? totalTmp : this.totalSum;
        },
        calcularTotalDosMeses() {
            const totalPorMesAnoRef = this.monthFields.map((obj) => {
                return this.getObjSumMonth(obj.mes, obj.ano);
            });
            this.calcularTotalGeral(totalPorMesAnoRef);
        },
        saveData() {
            let list = this.mapOnSave();
            this.isSaving = true;
            this.service
                .save(list)
                .then((r) => {
                    this.isUpdateVendaHeader = true;
                    this.$root.showMessage('success', 'msg-objetivo-save-success');
                })
                .catch((e) => {
                    console.error('Erro ao salvar objetivos de venda', e);
                    this.$root.showMessage('error', 'msg-objetivo-save-error');
                })
                .finally((_) => {
                    this.listaAlterados = [];
                    this.isAlterado = false;
                    this.isSaving = false;
                    this.loadData();
                });
        },
        tabSelectHandler(event) {
            if (event) {
                let keyCode = event.keyCode || event.which;
                let target = event.target;

                if (target && target.classList.contains('money-table-inputs') && keyCode == 9) {
                    target.select();
                }
            }
        },
        mapOnSave() {
            const data = this.listaAlterados.map((obj) => {
                const objetivo = {
                    mes: obj.mes,
                    ano: obj.ano,
                    objetivo: obj.valor,
                };

                return {
                    idusuario: obj.idusuario,
                    objetivo: objetivo,
                };
            });

            return data;
        },
        adjustTableWidth() {
            let tabela = document.querySelector('#objetivo-vendas-table');

            if (tabela) {
                let currentWidth = tabela.offsetWidth;

                if (window.innerWidth > 768) {
                    document.querySelector('#objetivo-vendas-table table.table').style.width =
                        this.calculatedTableWidth > currentWidth + this.widthTolerance
                            ? `${this.calculatedTableWidth}px`
                            : '';
                } else {
                    document.querySelector('#objetivo-vendas-table table.table').style.width = '';
                }
            }
        },
        generateTableColumnsFromInterval() {
            let fields = [];
            let ini = this.$moment(this.mesIni);
            let fim = this.$moment(this.mesFim);
            while (ini.isSameOrBefore(fim, 'month')) {
                fields.push({ mes: ini.month() + 1, ano: ini.year() });
                ini.add(1, 'month');
            }
            this.monthFields = fields;
        },
        checkIfBeforeCurrentMonth(mes, ano) {
            let moment = this.$moment()
                .month(mes - 1)
                .year(ano);

            if (moment) {
                return moment.isBefore(this.$moment(), 'month');
            } else {
                console.warn('Não foi possível criar moment.', mes - 1, ano);
                return false;
            }
        },
        getObjSumMonth(mes, ano) {
            const { valor, valorOld } = this.getSumForMonth(mes, ano);
            const obj = { mes, ano, valor, valorOld };
            return obj;
        },
        getSumForMonth(mes, ano) {
            const month = parseInt(mes);
            const year = parseInt(ano);

            let total = 0;
            let totalOld = 0;
            this.objetivoVendaHeader.forEach((obj) => {
                if (obj.mesReferencia == month && obj.anoReferencia == year) {
                    total = obj.totalMes;
                }
            });

            this.objetivoVendaHeaderOld.forEach((obj) => {
                if (obj.mesReferencia == month && obj.anoReferencia == year) {
                    totalOld = obj.totalMes;
                }
            });

            let valorTmp = 0;

            if (this.listaAlterados.length > 0) {
                this.listaAlterados.forEach((obj) => {
                    if (obj.mes == mes && obj.ano == ano) {
                        valorTmp = valorTmp + obj.valor;
                    }
                });
            }
            total = this.getDecimal(total + valorTmp, 2)
                .replaceAll('.', '')
                .replace(',', '.');
            totalOld = this.getDecimal(totalOld + valorTmp, 2)
                .replaceAll('.', '')
                .replace(',', '.');
            return { valor: total, valorOld: totalOld };
        },
        updateSortColumns(column, dir) {
            this.sortColumn = column;
            this.sortDirection = dir;
            this.loadData();
            this.updateDisplayTable();
        },
        calcularTotalGeral(totalPorMesAnoRef) {
            let totalTmp = 0;
            totalPorMesAnoRef.forEach((e) => {
                let valor = Number(e.valorOld);
                totalTmp = totalTmp + valor;
            });
            this.totalPorMesAno = totalPorMesAnoRef;
            this.totalSum = this.getDecimal(totalTmp, 2).replaceAll('.', '').replace(',', '.');
        },
        calcularTotalGeralPorColaborador() {
            let totalTmp = 0;
            this.totalPorMesAno.forEach((e) => {
                let valor = Number(e.valor.replace('.', '').replace(',', '.'));
                totalTmp = totalTmp + valor;
            });
            this.totalSumColaboradores = this.getDecimal(totalTmp, 2)
                .replaceAll('.', '')
                .replace(',', '.');
        },
        atualizarHeaderEMetas(mes, ano, id) {
            const header = this.objetivoVendaHeader.find(
                (obj) => obj.mesReferencia === mes && obj.anoReferencia === ano
            );
            if (header) {
                const vendasUser = this.tableData.filter(
                    (vu) =>
                        vu.usuario.idusuario === id &&
                        vu.objetivos &&
                        vu.objetivos.some((item) => item.mes === mes && item.ano === ano)
                );
                vendasUser.forEach((vendaUser) => {
                    const objetivo = vendaUser.objetivos.find(
                        (item) =>
                            item.mes == mes && item.ano == ano && vendaUser.usuario.idusuario == id
                    );
                    const diferenca = header.totalMes - objetivo.objetivo;
                    header.totalMes = diferenca;
                    vendaUser = null;
                });
            }
        },
        updateDisplayTable() {
            this.displayTableData = structuredClone(this.tableData);
            this.isEmpty = this.displayTableData.length <= 0;
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px 20px 20px;
    min-height: 100%;

    .page {
        position: relative;
        height: inherit;
        min-height: 100%;

        .nav.nav-tabs {
            margin-bottom: 0;
            margin-left: -20px;
            padding-left: 20px;
        }
        .registry-wrapper {
            padding: 5px;
            background-color: $sovis-white;
            box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
            border-radius: 5px;
        }
        .top-btn {
            height: 40px;
            padding: 0px 20px;
            margin-left: 0;
        }
    }
}
</style>
